import React, { useState, useEffect } from 'react';
import { Loader2, BookOpen } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { loadNameMeanings, loadProfile, resolveName } from '../utils/nameMeanings';
import ScriptureRefChips from './ScriptureRefChips';

const WORD_RE = /([A-Za-zÀ-ÿ'’]+)/g;

// A single tappable name: shows the Hitchcock meaning and can lazy-load an
// Easton's "Bible people" mini-profile (with cross-reference jumps) on demand.
const NameChip = ({ word, name, meaning, onNavigate }) => {
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const openProfile = async () => {
    setShowProfile(true);
    if (profile || loadingProfile) return;
    setLoadingProfile(true);
    setProfile(await loadProfile(name));
    setLoadingProfile(false);
  };

  const go = (book, chapter) => {
    setPopoverOpen(false);
    onNavigate && onNavigate(book, chapter);
  };

  return (
    <Popover open={popoverOpen} onOpenChange={(o) => { setPopoverOpen(o); if (!o) setShowProfile(false); }}>
      <PopoverTrigger asChild>
        <span
          className="cursor-pointer font-medium text-amber-700 dark:text-amber-300 underline decoration-dotted decoration-amber-500 underline-offset-2 hover:bg-amber-100 dark:hover:bg-amber-900/40 rounded px-0.5 transition-colors"
          data-testid={`name-chip-${word.toLowerCase()}`}
          onClick={(e) => e.stopPropagation()}
        >
          {word}
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-72" data-testid="name-meaning-popover">
        <p className="font-semibold text-sm">{name}</p>
        <p className="text-xs text-muted-foreground mt-1 italic">"{meaning}"</p>
        {!showProfile ? (
          <button
            onClick={openProfile}
            className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300 hover:underline"
            data-testid={`name-profile-btn-${word.toLowerCase()}`}
          >
            <BookOpen className="w-3.5 h-3.5" /> View profile
          </button>
        ) : (
          <div className="mt-3 border-t pt-2" data-testid="name-profile-content">
            {loadingProfile ? (
              <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
            ) : (
              <>
                <p className="text-xs text-foreground/90 max-h-32 overflow-y-auto leading-relaxed">{profile?.definition}</p>
                <ScriptureRefChips refs={profile?.refs} onNavigate={go} />
              </>
            )}
          </div>
        )}
        <p className="text-[10px] text-muted-foreground/70 mt-2">Hitchcock's Bible Names · Easton's</p>
      </PopoverContent>
    </Popover>
  );
};

/**
 * NameMeaningText — renders verse text where recognized biblical proper names
 * get a subtle dotted underline; tapping reveals the meaning + an optional
 * Easton's mini-profile. Non-name text renders normally.
 */
const NameMeaningText = ({ text, className = '', onNavigate }) => {
  const [names, setNames] = useState({});

  useEffect(() => {
    let cancelled = false;
    loadNameMeanings().then((m) => { if (!cancelled) setNames(m); });
    return () => { cancelled = true; };
  }, []);

  if (!text) return null;

  const parts = [];
  let last = 0;
  let m;
  WORD_RE.lastIndex = 0;
  while ((m = WORD_RE.exec(text)) !== null) {
    const word = m[1];
    const start = m.index;
    if (start > last) parts.push(text.slice(last, start));
    const isCapitalized = word[0] === word[0].toUpperCase() && word[0] !== word[0].toLowerCase();
    // Match the name itself, its possessive form, or a KJV genealogy alias.
    const entry = resolveName(word, names);
    if (entry && isCapitalized && word.length >= 3) {
      parts.push({ word, meaning: entry.meaning, name: entry.name, key: `${start}` });
    } else {
      parts.push(word);
    }
    last = start + word.length;
  }
  if (last < text.length) parts.push(text.slice(last));

  return (
    <span className={className} data-testid="name-meaning-text">
      {parts.map((p, i) =>
        typeof p === 'string'
          ? <span key={i}>{p}</span>
          : <NameChip key={i} word={p.word} name={p.name} meaning={p.meaning} onNavigate={onNavigate} />
      )}
    </span>
  );
};

export default NameMeaningText;
